import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, throwError } from 'rxjs';
import { switchMap, catchError, finalize, shareReplay } from 'rxjs/operators';
import { of } from 'rxjs';
import { CacheService } from './cache.service';
import { NavigationExtras, Router } from '@angular/router';
import { ApiEndpoints } from '../../constants/api.endpoints';
import { normalizeRole } from '../../shared/utils/role.utils';
import {
  UserRegistrationRequest,
  UserRegistrationResponse,
  LoginRequest,
  LoginResponse,
  PersonaOptionsData,
} from '../../shared/types/auth';
import {
  ReferralOverview,
  ReferralProgram,
  ReferralRewardConditionsSettings,
  ReferralFeature,
  ReferralFeatureRole,
} from '../../shared/types/referral';
import { LocationService } from './location.service';
import {
  formatRmaErrorMessage,
  RmaApiErrorPayload,
} from '../utils/api-error.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private rolePermissionService: any; // Will be injected lazily to avoid circular dependency

  private readonly TOKEN_KEY = 'rma_token';
  private readonly REFRESH_TOKEN_KEY = 'rma_refresh_token';
  private readonly USER_KEY = 'rma_user';
  private readonly REFERRAL_CODE_KEY = 'rma_referral_code';
  private locationCacheInit$: Observable<boolean> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private cacheService: CacheService,
    private locationService: LocationService
  ) {
    // Initialize user from localStorage if available
    this.initializeFromStorage();

    // Initialize location cache only for authenticated sessions
    if (this.shouldInitializeLocationCache()) {
      this.initializeLocationCache();
    }
  }
  initializeLocationCache() {
    this.getLocationCacheLoad$().subscribe();
  }

  private shouldInitializeLocationCache(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!token && !!user;
  }

  private getLocationCacheLoad$(): Observable<boolean> {
    const cachedCountries = this.cacheService.getCountries();

    if (cachedCountries?.length) {
      return of(true);
    }

    if (this.locationCacheInit$) {
      return this.locationCacheInit$;
    }

    this.locationCacheInit$ = this.locationService.getCountries().pipe(
      tap(response => {
        const countries = response?.response || response?.data;
        if (!countries) {
          return;
        }

        this.cacheService.setCountries(countries);
      }),
      switchMap(response => {
        const countries = response?.response || response?.data || [];
        return of(countries.length > 0);
      }),
      catchError(error => {
        console.error(
          '[AuthService] Location cache initialization failed:',
          error
        );
        return of(false);
      }),
      finalize(() => {
        this.locationCacheInit$ = null;
      }),
      shareReplay(1)
    );

    return this.locationCacheInit$;
  }

  /**
   * Resend verification email to user
   */
  resendVerificationEmail(email: string): Observable<any> {
    const url =
      ApiEndpoints.BASE_URL + ApiEndpoints.Users + '/resend-verification';
    return this.http
      .post<any>(url, { email }, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }
  /**
   * Register a new user (tenant or landlord)
   */
  register(
    userData: UserRegistrationRequest
  ): Observable<UserRegistrationResponse> {
    const url = ApiEndpoints.BASE_URL + ApiEndpoints.Registeration;
    const referralCode =
      userData.referralCode || this.getStoredReferralCode() || '';

    // Prepare the request payload according to backend DTO
    const requestPayload = {
      fullName: userData.fullName,
      email: userData.email,
      password: userData.password,
      role: userData.role, // This maps to 'roleName' in the DTO via @JsonProperty
      phoneNumber: userData.phoneNumber || '',
      gender: userData.gender || '',
      selectedCountry: userData.selectedCountry || '',
      selectedState: userData.selectedState || '',
      selectedCity: userData.selectedCity || '',
      address: userData.address || '',
      zipCode: userData.zipCode || '',
      referralCode,
      termsAccepted: userData.termsAccepted === true,
    };

    return this.http
      .post<UserRegistrationResponse>(url, requestPayload, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response => {
          console.log('Registration successful:', response);
          if (response?.successful) {
            this.clearStoredReferralCode();
          }
        }),
        catchError(this.handleError)
      );
  }

  storeReferralCode(referralCode: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    const normalizedCode = referralCode.trim().toUpperCase();
    if (!normalizedCode) {
      return;
    }
    localStorage.setItem(this.REFERRAL_CODE_KEY, normalizedCode);
  }

  getStoredReferralCode(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(this.REFERRAL_CODE_KEY);
  }

  clearStoredReferralCode(): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem(this.REFERRAL_CODE_KEY);
  }

  getReferralOverview(): Observable<{
    response: ReferralOverview;
    successful: boolean;
    message: string;
  }> {
    const url = ApiEndpoints.BASE_URL + ApiEndpoints.ReferralOverview;
    return this.http
      .get<{
        response: ReferralOverview;
        successful: boolean;
        message: string;
      }>(url, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(httpError => this.handleError(httpError)));
  }

  getReferralPrograms(): Observable<{
    response: ReferralProgram[];
    successful: boolean;
    message: string;
  }> {
    const url = ApiEndpoints.BASE_URL + ApiEndpoints.ReferralPrograms;
    return this.http
      .get<{
        response: ReferralProgram[];
        successful: boolean;
        message: string;
      }>(url, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(httpError => this.handleError(httpError)));
  }

  getReferralRewardConditions(): Observable<{
    response: ReferralRewardConditionsSettings;
    successful: boolean;
    message: string;
  }> {
    const url = ApiEndpoints.BASE_URL + ApiEndpoints.ReferralRewardConditions;
    return this.http
      .get<{
        response: ReferralRewardConditionsSettings;
        successful: boolean;
        message: string;
      }>(url, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(httpError => this.handleError(httpError)));
  }

  getReferralFeatures(role: ReferralFeatureRole): Observable<{
    response: ReferralFeature[];
    successful: boolean;
    message: string;
  }> {
    const url =
      ApiEndpoints.BASE_URL +
      ApiEndpoints.ReferralFeatures +
      `?role=${encodeURIComponent(role)}`;
    return this.http
      .get<{
        response: ReferralFeature[];
        successful: boolean;
        message: string;
      }>(url, { headers: this.getHeaders() })
      .pipe(catchError(httpError => this.handleError(httpError)));
  }

  /**
   * Login user
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    const url = ApiEndpoints.BASE_URL + ApiEndpoints.Login;

    return this.http
      .post<LoginResponse>(url, credentials, {
        headers: this.getHeaders(),
      })
      .pipe(
        switchMap((response: LoginResponse) => {
          if (this.applyAuthResponse(response)) {
            return this.getLocationCacheLoad$().pipe(
              switchMap(() => of(response))
            );
          }

          return throwError(() =>
            this.buildBusinessError(response, 'Login failed')
          );
        }),
        catchError(httpError => {
          // Only handle actual HTTP errors here, not business logic errors
          console.error('Auth Service HTTP Error:', httpError);
          return this.handleError(httpError);
        })
      );
  }

  refreshSession(): Observable<LoginResponse> {
    const url = ApiEndpoints.BASE_URL + ApiEndpoints.SessionRefresh;

    return this.http
      .post<LoginResponse>(url, {}, { headers: this.getHeaders() })
      .pipe(
        switchMap((response: LoginResponse) => {
          if (this.applyAuthResponse(response)) {
            return of(response);
          }

          return throwError(() =>
            this.buildBusinessError(response, 'Failed to refresh session')
          );
        }),
        catchError(httpError => this.handleError(httpError))
      );
  }

  startTenantImpersonation(reason?: string): Observable<LoginResponse> {
    const url = ApiEndpoints.BASE_URL + '/api/users/impersonation/start';
    const payload = { reason: reason || null };

    return this.http
      .post<LoginResponse>(url, payload, { headers: this.getHeaders() })
      .pipe(
        switchMap((response: LoginResponse) => {
          if (this.applyAuthResponse(response)) {
            return of(response);
          }

          return throwError(() =>
            this.buildBusinessError(
              response,
              'Failed to start tenant impersonation'
            )
          );
        }),
        catchError(httpError => this.handleError(httpError))
      );
  }

  stopImpersonation(reason?: string): Observable<LoginResponse> {
    const url = ApiEndpoints.BASE_URL + '/api/users/impersonation/stop';
    const payload = { reason: reason || null };

    return this.http
      .post<LoginResponse>(url, payload, { headers: this.getHeaders() })
      .pipe(
        switchMap((response: LoginResponse) => {
          if (this.applyAuthResponse(response)) {
            return of(response);
          }

          return throwError(() =>
            this.buildBusinessError(response, 'Failed to stop impersonation')
          );
        }),
        catchError(httpError => this.handleError(httpError))
      );
  }

  getPersonaOptions(): Observable<{
    response: PersonaOptionsData;
    successful: boolean;
    message: string;
  }> {
    const url = ApiEndpoints.BASE_URL + '/api/users/persona/options';
    return this.http
      .get<{
        response: PersonaOptionsData;
        successful: boolean;
        message: string;
      }>(url, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(httpError => this.handleError(httpError)));
  }

  isImpersonating(): boolean {
    return !!this.getCurrentUser()?.impersonating;
  }

  /**
   * Logout user
   */
  logout(options?: {
    redirectTo?: string;
    navigationExtras?: NavigationExtras;
  }): void {
    console.log('🚪 LOGOUT CALLED - Stack trace:');
    console.trace('Logout called from:');

    // Prevent logout during SSR
    if (typeof window === 'undefined') {
      console.log('🚫 SSR detected - skipping logout (no browser environment)');
      return;
    }

    // Clear server-side permissions cache
    this.clearServerPermissionsCache();

    // Clear frontend permissions cache when logging out
    this.invalidateFrontendPermissionsCache();

    if (this.rolePermissionService) {
      this.rolePermissionService.clearAllCache();
    }

    // Clear all authentication data
    this.clearLocalStorage();

    // Reset the current user subject
    this.currentUserSubject.next(null);

    const redirectTo = options?.redirectTo || '/login';

    console.log('🚪 Navigating to login page...', {
      redirectTo,
      navigationExtras: options?.navigationExtras || null,
    });
    this.router.navigate([redirectTo], options?.navigationExtras);
  }

  private applyAuthResponse(response: LoginResponse): boolean {
    if (
      !(response.successful && response.response && response.response.token)
    ) {
      return false;
    }

    this.setToken(response.response.token);
    this.setRefreshToken(response.response.refreshToken || null);

    const normalizedRole =
      normalizeRole(response.response.role) || response.response.role;
    const normalizedImpersonatedByRole =
      normalizeRole(response.response.impersonatedByRole) ||
      response.response.impersonatedByRole;

    const user = {
      id: response.response.userId,
      userId: response.response.userId,
      fullName: response.response.fullName,
      email: response.response.email,
      role: normalizedRole,
      roleName: normalizedRole,
      verified: response.response.verified,
      impersonating: !!response.response.impersonating,
      impersonationSessionId: response.response.impersonationSessionId || null,
      impersonatedByUserId: response.response.impersonatedByUserId || null,
      impersonatedByEmail: response.response.impersonatedByEmail || null,
      impersonatedByRole: normalizedImpersonatedByRole || null,
      requiresOnboarding: response.response.requiresOnboarding,
      stripeAccountId: response.response.stripeAccountId,
      onboardingUrl: response.response.onboardingUrl,
      registeredAt: response.response.registeredAt || null,
      assignedProperties: this.parseAssignedProperties(
        response.response.assignedProperties ?? null
      ),
      assignedLandlordId: response.response.assignedLandlordId || null,
    };

    this.setUser(user);
    this.currentUserSubject.next(user);
    this.invalidateFrontendPermissionsCache();
    return true;
  }

  private buildBusinessError(
    response: LoginResponse,
    fallbackMessage: string
  ): any {
    const errorMessage = formatRmaErrorMessage(response, fallbackMessage);

    return {
      message: errorMessage,
      status: response.statusCode,
      statusText: response.message,
      statusCode: response.statusCode,
      code: response.code ?? null,
      error: {
        code: response.code ?? null,
        message: response.message,
        data: response.data ?? response.response ?? null,
        errors: response.errors ?? null,
      },
      errors: response.errors || [],
      timestamp: response.timestamp,
      successful: false,
    };
  }

  private invalidateFrontendPermissionsCache(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(
          'permissions_cache_invalidated',
          Date.now().toString()
        );
      }
    } catch (err) {
      console.warn('Could not invalidate permissions cache:', err);
    }
  }

  /**
   * Clear server-side permissions cache
   */
  private clearServerPermissionsCache(): void {
    const url = ApiEndpoints.BASE_URL + '/api/permissions/cache';

    this.http
      .delete(url, {
        headers: this.getHeaders(),
        responseType: 'text', // Expect text response
      })
      .subscribe({
        next: response => {
          console.log(
            '✅ Server permissions cache cleared successfully:',
            response
          );
        },
        error: error => {
          console.warn(
            '⚠️ Failed to clear server permissions cache (non-critical):',
            error
          );
          // Don't block logout process if cache clearing fails
        },
      });
  }

  /**
   * Clear localStorage data safely
   */
  private clearLocalStorage(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      console.log('🗑️ Clearing localStorage...');
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(this.USER_KEY);

      // Clear cache keys
      localStorage.removeItem('paymentSettingsCache');
      localStorage.removeItem('smsSettingsCache');
      localStorage.removeItem('support_permission_codes');
      localStorage.removeItem('support_page_keys');

      // Clear any other auth-related data that might exist
      localStorage.removeItem('authToken'); // Legacy key if exists
    }
  }

  /**
   * Log completion message
   */
  private logLogoutComplete(): void {
    console.log('User logged out successfully');
  }

  /**
   * Switch user role (for Super Admin only)
   */
  async switchUserRole(newRole: string): Promise<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.isSuperAdmin(currentUser.role)) {
      console.warn('Role switching not allowed for current user');
      return false;
    }

    try {
      // Update user object with new role
      const updatedUser = {
        ...currentUser,
        role: normalizeRole(newRole) || newRole,
        originalRole: currentUser.originalRole || currentUser.role, // Keep track of original role
      };

      // Update storage and subjects
      this.setUser(updatedUser);
      this.currentUserSubject.next(updatedUser);

      // Clear permissions cache to reload for new role
      if (this.rolePermissionService) {
        this.rolePermissionService.clearUserCache(currentUser.id);
      }

      console.log(`Role switched from ${currentUser.role} to ${newRole}`);
      return true;
    } catch (error) {
      console.error('Error switching role:', error);
      return false;
    }
  }

  /**
   * Restore original role (for Super Admin only)
   */
  async restoreOriginalRole(): Promise<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.originalRole) {
      return false;
    }

    try {
      const restoredUser = {
        ...currentUser,
        role:
          normalizeRole(currentUser.originalRole) || currentUser.originalRole,
        originalRole: undefined, // Remove original role tracking
      };

      this.setUser(restoredUser);
      this.currentUserSubject.next(restoredUser);

      // Clear permissions cache
      if (this.rolePermissionService) {
        this.rolePermissionService.clearUserCache(currentUser.id);
      }

      console.log(`Role restored to ${currentUser.originalRole}`);
      return true;
    } catch (error) {
      console.error('Error restoring role:', error);
      return false;
    }
  }

  /**
   * Check if current user is super admin
   */
  isSuperAdmin(role?: string): boolean {
    const userRole = role || this.getCurrentUser()?.role;
    if (!userRole) return false;
    const normalizedRole = normalizeRole(userRole);
    return normalizedRole === 'SUPER_ADMIN' || normalizedRole === 'ADMIN';
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();

    console.log('🔐 isAuthenticated check:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      hasUser: !!user,
      userEmail: user?.email || 'none',
      tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
    });

    // Must have both token and user data
    if (!token || !user) {
      console.log('❌ Authentication failed:', {
        reason: !token ? 'Missing token' : 'Missing user',
        willClearAuth: true,
      });
      this.clearInvalidAuth();
      return false;
    }

    console.log('✅ Authentication successful');
    // Additional validation can be added here (token expiration, etc.)
    return true;
  }

  /**
   * Clear invalid authentication data
   */
  private clearInvalidAuth(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(this.USER_KEY);
      localStorage.removeItem('paymentSettingsCache');
      localStorage.removeItem('smsSettingsCache');
      localStorage.removeItem('support_permission_codes');
      localStorage.removeItem('support_page_keys');
      localStorage.removeItem('authToken'); // Legacy key if exists
    }
    this.currentUserSubject.next(null);
  }

  /**
   * Get current user (from memory first, then localStorage)
   */
  getCurrentUser(): any {
    // First check in-memory current user
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      return currentUser;
    }

    // Fallback to localStorage if available
    if (typeof window !== 'undefined' && window.localStorage) {
      const userStr = localStorage.getItem(this.USER_KEY);
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          user.role = normalizeRole(user.role) || user.role;
          user.roleName =
            normalizeRole(user.roleName || user.role) ||
            user.roleName ||
            user.role;
          // Parse assignedProperties if stored as JSON string
          if (
            user.assignedProperties &&
            typeof user.assignedProperties === 'string'
          ) {
            user.assignedProperties = this.parseAssignedProperties(
              user.assignedProperties
            );
          }
          // Update the subject with the stored user
          this.currentUserSubject.next(user);
          console.log(
            '💾 Restored user from localStorage for auth check:',
            user.username || user.email
          );
          return user;
        } catch (e) {
          console.error('❌ Error parsing user from localStorage:', e);
          this.clearInvalidAuth();
        }
      }
    }
    return null;
  }

  /**
   * Parse assignedProperties JSON string to array
   */
  private parseAssignedProperties(
    assignedPropertiesJson: string | null
  ): string[] | null {
    if (!assignedPropertiesJson) {
      return null;
    }
    try {
      const parsed = JSON.parse(assignedPropertiesJson);
      return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      console.warn(
        '[AuthService] Failed to parse assignedProperties JSON:',
        assignedPropertiesJson,
        error
      );
      return null;
    }
  }

  /**
   * Update current user profile (e.g., after profile image upload)
   */
  updateCurrentUser(updatedProfile: any): void {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      // Merge updated profile data with existing user data
      const updatedUser = { ...currentUser, ...updatedProfile };

      // Update in-memory user
      this.currentUserSubject.next(updatedUser);

      // Update localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser));
      }

      console.log('✅ Updated current user profile:', updatedUser);
    }
  }

  /**
   * Get stored token (public for debugging)
   */
  getToken(): string | null {
    if (typeof window !== 'undefined' && window.localStorage) {
      const token = localStorage.getItem(this.TOKEN_KEY);
      console.log('🔍 Retrieved token from localStorage:', {
        exists: !!token,
        length: token?.length || 0,
        preview: token ? token.substring(0, 20) + '...' : 'null',
        key: this.TOKEN_KEY,
      });
      return token;
    }
    console.log('❌ Cannot retrieve token - localStorage unavailable');
    return null;
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    return null;
  }

  /**
   * Set token in storage
   */
  private setToken(token: string): void {
    console.log(
      '💾 Saving token to localStorage:',
      token ? token.substring(0, 20) + '...' : 'null'
    );
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(this.TOKEN_KEY, token);
      console.log('✅ Token saved successfully');
    } else {
      console.log('❌ Cannot save token - localStorage unavailable');
    }
  }

  /**
   * Set refresh token in storage
   */
  private setRefreshToken(refreshToken: string | null): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (refreshToken) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      } else {
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      }
    }
  }

  /**
   * Set user in storage
   */
  private setUser(user: any): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
  }

  /**
   * Initialize user from localStorage
   */
  private initializeFromStorage(): void {
    if (typeof window !== 'undefined') {
      const token =
        localStorage.getItem(this.TOKEN_KEY) ||
        sessionStorage.getItem(this.TOKEN_KEY);
      const user =
        localStorage.getItem(this.USER_KEY) ||
        sessionStorage.getItem(this.USER_KEY);

      if (token && user) {
        try {
          const parsedUser = JSON.parse(user);
          // Normalize the role
          if (parsedUser.role) {
            parsedUser.role = normalizeRole(parsedUser.role);
          }
          this.currentUserSubject.next(parsedUser);
        } catch (e) {
          console.error('Error parsing user from storage:', e);
          this.logout();
        }
      }
    }
  }

  /**
   * Get HTTP headers
   */
  public getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const token = this.getToken();
    if (token) {
      return headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Get HTTP headers for file uploads (without Content-Type)
   */
  public getFileUploadHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Handle HTTP errors
   */
  private handleError = (error: any): Observable<never> => {
    console.error('Auth Service Error:', error);

    let errorMessage = 'An unknown error occurred';
    let payload: RmaApiErrorPayload | null = null;

    if (error.error && typeof error.error === 'object') {
      payload = error.error;
      errorMessage = formatRmaErrorMessage(payload, errorMessage);
    } else if (error.error && typeof error.error === 'string') {
      payload = { message: error.error };
      errorMessage = formatRmaErrorMessage(payload, errorMessage);
    } else if (error?.message) {
      payload = {
        code: error?.code ?? null,
        message: error?.message,
        data: error?.data,
        errors: error?.errors,
      };
      errorMessage = formatRmaErrorMessage(payload, error?.message);
    }

    // Handle specific status codes if it's an actual HTTP error
    if (error.status === 401) {
      errorMessage = errorMessage || 'Invalid credentials';
    } else if (error.status === 403) {
      errorMessage =
        errorMessage || 'Please verify your email before logging in';
    } else if (error.status === 500) {
      errorMessage =
        errorMessage || 'Server error occurred. Please try again later.';
    }

    return throwError(() => ({
      message: errorMessage,
      status: error.status || 0,
      statusText: error.statusText || 'Unknown Error',
      code: payload?.code ?? error?.code ?? null,
      error: payload
        ? payload
        : {
            code: error?.code ?? null,
            message: error?.message ?? errorMessage,
            data: error?.data ?? null,
            errors: error?.errors ?? null,
          },
    }));
  };
}

// import { Injectable } from '@angular/core';
// import { HttpClient, HttpHeaders } from '@angular/common/http';
// import { Observable, BehaviorSubject, tap, catchError, throwError, switchMap, of } from 'rxjs';
// import { Router } from '@angular/router';
// import { ApiEndpoints } from '../../constants/api.endpoints';
// import {
//   UserRegistrationRequest,
//   UserRegistrationResponse,
//   LoginRequest,
//   LoginResponse
// } from '../../shared/types/auth.types';

// @Injectable({
//   providedIn: 'root'
// })
// export class AuthService {
//   private currentUserSubject = new BehaviorSubject<any>(null);
//   public currentUser$ = this.currentUserSubject.asObservable();

//   private readonly TOKEN_KEY = 'rma_token';
//   private readonly USER_KEY = 'rma_user';

//   constructor(
//     private http: HttpClient,
//     private router: Router
//   ) {
//     // Initialize user from localStorage if available
//     this.initializeFromStorage();
//   }

//   /**
//    * Register a new user (tenant or landlord)
//    */
//   register(userData: UserRegistrationRequest): Observable<UserRegistrationResponse> {
//     const url = ApiEndpoints.BASE_URL + ApiEndpoints.Registeration;

//     // Prepare the request payload according to backend DTO
//     const requestPayload = {
//       fullName: userData.fullName,
//       email: userData.email,
//       password: userData.password,
//       role: userData.role, // This maps to 'roleName' in the DTO via @JsonProperty
//       phoneNumber: userData.phoneNumber || '',
//       gender: userData.gender || '',
//       selectedCountry: userData.selectedCountry || '',
//       selectedState: userData.selectedState || '',
//       selectedCity: userData.selectedCity || '',
//       address: userData.address || '',
//       zipCode: userData.zipCode || ''
//     };

//     return this.http.post<UserRegistrationResponse>(url, requestPayload, {
//       headers: this.getHeaders()
//     }).pipe(
//       tap((response) => {
//         console.log('Registration successful:', response);
//       }),
//       catchError(this.handleError)
//     );
//   }

//   /**
//    * Login user
//    */
//   login(credentials: LoginRequest): Observable<LoginResponse> {
//     const url = ApiEndpoints.BASE_URL + ApiEndpoints.Login;

//     return this.http.post<LoginResponse>(url, credentials, {
//       headers: this.getHeaders()
//     }).pipe(
//       switchMap((response: LoginResponse) => {
//         // Handle successful responses
//         if (response.successful && response.response && response.response.token) {
//           console.log('Login successful:', response);
//           this.setToken(response.response.token);
//           // Create user object from response data
//           const user = {
//             id: response.response.userId,
//             fullName: response.response.fullName,
//             email: response.response.email,
//             role: response.response.role,
//             verified: response.response.verified
//           };
//           this.setUser(user);
//           this.currentUserSubject.next(user);
//           return of(response);
//         } else {
//           // Handle unsuccessful responses (backend returned successful: false)
//           const errorMsg = response.errors && response.errors.length > 0
//             ? response.errors[0]
//             : response.message || 'Login failed';
//           const error = {
//             message: errorMsg,
//             status: response.statusCode,
//             statusText: response.message,
//             statusCode: response.statusCode,
//             errors: response.errors || [],
//             timestamp: response.timestamp,
//             successful: false
//           };
//           return throwError(() => error);
//         }
//       }),
//       catchError((httpError) => {
//         // Only handle actual HTTP errors here, not business logic errors
//         console.error('Auth Service HTTP Error:', httpError);
//         return this.handleError(httpError);
//       })
//     );
//   }

//   /**
//    * Logout user
//    */
//   logout(): void {
//     localStorage.removeItem(this.TOKEN_KEY);
//     localStorage.removeItem(this.USER_KEY);
//     this.currentUserSubject.next(null);
//     this.router.navigate(['/login']);
//   }

//   /**
//    * Check if user is authenticated
//    */
//   isAuthenticated(): boolean {
//     const token = this.getToken();
//     if (!token) return false;

//     // You might want to add token expiration check here
//     return true;
//   }

//   /**
//    * Get current user
//    */
//   getCurrentUser(): any {
//     return this.currentUserSubject.value;
//   }

//   /**
//    * Get stored token
//    */
//   getToken(): string | null {
//     return localStorage.getItem(this.TOKEN_KEY);
//   }

//   /**
//    * Set token in storage
//    */
//   private setToken(token: string): void {
//     localStorage.setItem(this.TOKEN_KEY, token);
//   }

//   /**
//    * Set user in storage
//    */
//   private setUser(user: any): void {
//     localStorage.setItem(this.USER_KEY, JSON.stringify(user));
//   }

//   /**
//    * Initialize user from localStorage
//    */
//   private initializeFromStorage(): void {
//     const token = this.getToken();
//     const userStr = localStorage.getItem(this.USER_KEY);

//     if (token && userStr) {
//       try {
//         const user = JSON.parse(userStr);
//         this.currentUserSubject.next(user);
//       } catch (error) {
//         console.error('Error parsing stored user data:', error);
//         this.logout();
//       }
//     }
//   }

//   /**
//    * Get HTTP headers
//    */
//   public getHeaders(): HttpHeaders {
//     const headers = new HttpHeaders({
//       'Content-Type': 'application/json'
//     });

//     const token = this.getToken();
//     if (token) {
//       return headers.set('Authorization', `Bearer ${token}`);
//     }

//     return headers;
//   }

//   /**
//    * Handle HTTP errors
//    */
//     if (error.status === 401) {
//       errorMessage = errorMessage || 'Invalid credentials';
//     } else if (error.status === 403) {
//       errorMessage = errorMessage || 'Please verify your email before logging in';
//     } else if (error.status === 500) {
//       errorMessage = errorMessage || 'Server error occurred. Please try again later.';
//     }

//     return throwError(() => ({
//       message: errorMessage,
//       status: error.status || 0,
//       statusText: error.statusText || 'Unknown Error'
//     }));
//   };
// }
